import React, { useEffect, useState, useMemo } from 'react';
import {
  Users,
  GraduationCap,
  UserCog,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Filter,
  X,
  FileSpreadsheet,
  FileArchive,
  Loader2,
  AlertCircle,
  Play,
  History,
  ChevronDown,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import { fetchActiveCampuses, CampusOption } from '@/services/userService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { getAcademicYears, AcademicYear } from '@/services/academicPlanningService';
import { Permission } from '@/types/global.types';
import { formatAcademicYearName } from '@/utils/academicYear';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`;

function getAuthHeaders(): Record<string, string> {
  const tokens = useAuthStore.getState().tokens;
  const user = useAuthStore.getState().user;

  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(user?.tenantId ? { 'X-Tenant-ID': user.tenantId } : {}),
    ...(tokens?.accessToken ? { 'Authorization': `Bearer ${tokens.accessToken}` } : {}),
  };
}

// History item interface for ministry documents generation
interface MinistryHistoryItem {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  totalItems: number;
  processedItems: number;
  zipFileUrl: string | null;
  excelFileUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  reportType: 'STAFF' | 'TEACHERS' | 'STUDENTS';
  academicYearId: string | null;
  academicYearName: string | null;
  requestedByName: string;
}

// Generation status interface for ministry documents
interface GenerationStatus {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'NONE';
  progress: number;
  totalItems: number;
  processedItems: number;
  zipFileUrl: string | null;
  excelFileUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  reportType: string;
}

// Report type for ministry format
type MinistryReportType = 'ADMIN_STAFF' | 'TEACHERS' | 'STUDENTS';

const ministryReportTypeConfig = {
  ADMIN_STAFF: {
    label: 'Personnel Administratif',
    icon: UserCog,
    color: 'emerald',
    description: 'Personnel administratif et technique',
    columns: ['N CNIE', 'N Passeport', 'Nom', 'Prenom', 'Sexe', 'Date Naissance', 'Pays', 'Fonction', 'Handicape'],
    apiType: 'ADMIN_STAFF',
  },
  TEACHERS: {
    label: 'Enseignants',
    icon: Users,
    color: 'purple',
    description: 'Corps enseignant',
    columns: ['N CNIE', 'N Passeport', 'Nom', 'Prenom', 'Nom Arabe', 'Prenom Arabe', 'Sexe', 'Date Naissance', 'Nationalite', 'Statut', 'Departement', 'Grade', 'Diplome', 'Specialite', 'Date Recrutement', 'Matieres', 'Handicape'],
    apiType: 'TEACHERS',
  },
  STUDENTS: {
    label: 'Etudiants',
    icon: GraduationCap,
    color: 'blue',
    description: 'Etudiants inscrits',
    columns: ['N CNIE', 'N Passeport', 'Nom', 'Prenom', 'Sexe', 'Date Naissance', 'Pays', 'Filiere', 'Niveau', 'Handicape'],
    apiType: 'STUDENTS',
  },
};

// History Item Component
const HistoryItem: React.FC<{
  item: MinistryHistoryItem;
  downloadingHistoryId: string | null;
  downloadingHistoryType: 'zip' | 'excel' | null;
  onDownloadExcel: (id: string) => void;
  onDownloadZip: (id: string) => void;
}> = ({ item, downloadingHistoryId, downloadingHistoryType, onDownloadExcel, onDownloadZip }) => {
  const reportTypeLabels: Record<string, string> = {
    STAFF: 'Personnel Administratif',
    TEACHERS: 'Enseignants',
    STUDENTS: 'Etudiants',
  };

  return (
    <div className="p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {reportTypeLabels[item.reportType] || item.reportType}
          </p>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span>Par {item.requestedByName}</span>
            <span>{new Date(item.createdAt).toLocaleString('fr-FR')}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              item.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
              item.status === 'FAILED' ? 'bg-red-100 text-red-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {item.status === 'COMPLETED' ? 'Termine' :
               item.status === 'FAILED' ? 'Echoue' :
               'En cours'}
            </span>
            <span className="text-xs text-gray-400">{item.totalItems} dossiers</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {item.status === 'COMPLETED' && (
            <>
              <button
                onClick={() => onDownloadExcel(item.id)}
                disabled={downloadingHistoryId === item.id}
                className="p-1.5 text-green-600 bg-green-50 hover:bg-green-100 hover:shadow rounded transition-all disabled:opacity-50 disabled:cursor-wait"
                title="Telecharger Excel"
              >
                {downloadingHistoryId === item.id && downloadingHistoryType === 'excel' ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => onDownloadZip(item.id)}
                disabled={downloadingHistoryId === item.id}
                className="p-1.5 text-purple-600 bg-purple-50 hover:bg-purple-100 hover:shadow rounded transition-all disabled:opacity-50 disabled:cursor-wait"
                title="Telecharger ZIP (PDFs)"
              >
                {downloadingHistoryId === item.id && downloadingHistoryType === 'zip' ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <FileArchive className="w-4 h-4" />
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const DocumentsEdepPage: React.FC = () => {
  const { hasPermission } = useAuthStore();

  // Permission checks
  const canView = hasPermission(Permission.EDEP_VIEW);
  const canGenerate = hasPermission(Permission.EDEP_GENERATE);
  const canExport = hasPermission(Permission.EDEP_EXPORT);
  const canViewHistory = hasPermission(Permission.EDEP_HISTORY_VIEW);

  // Common state
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Ministry export state — honor a `?tab=` query param (used by the E-DEP
  // "report ready" notification to open directly on the right report type).
  const initialTab = ((): MinistryReportType => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab === 'TEACHERS' || tab === 'STUDENTS' || tab === 'ADMIN_STAFF'
      ? tab
      : 'ADMIN_STAFF';
  })();
  const [selectedMinistryType, setSelectedMinistryType] = useState<MinistryReportType>(initialTab);
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus | null>(null);
  const [pollingGenerationId, setPollingGenerationId] = useState<string | null>(null);
  const [includeMinistryComplete, setIncludeMinistryComplete] = useState(true);
  // Default TRUE: the preview/generation must cover EVERYONE (complete + incomplete),
  // so the count matches the personnel module. Untick to restrict.
  const [includeMinistryIncomplete, setIncludeMinistryIncomplete] = useState(true);
  const [ministryData, setMinistryData] = useState<any[]>([]);
  const [ministryStats, setMinistryStats] = useState<any>(null);
  const [loadingMinistryData, setLoadingMinistryData] = useState(false);
  const [generating, setGenerating] = useState(false);

  // History
  const [history, setHistory] = useState<MinistryHistoryItem[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [downloadingHistoryId, setDownloadingHistoryId] = useState<string | null>(null);
  const [downloadingHistoryType, setDownloadingHistoryType] = useState<'zip' | 'excel' | null>(null);
  const [downloadingMain, setDownloadingMain] = useState<'zip' | 'excel' | null>(null);

  // History modal expandable sections
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    ADMIN_STAFF: true,
    TEACHERS: false,
    STUDENTS: false,
  });
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load ministry data when type or filters change
  useEffect(() => {
    if (selectedAcademicYearId) {
      loadMinistryData();
      loadLatestGeneration();
    }
  }, [selectedMinistryType, selectedCampusId, selectedAcademicYearId]);

  // Poll for generation status
  useEffect(() => {
    if (!pollingGenerationId) return;

    // A generation runs inside the API process, so a restart — every deploy is
    // one — kills it mid-flight. Without a bound, this loop asks for news of a
    // job that will never answer, every three seconds, for as long as the page
    // stays open. The server closes abandoned jobs when it comes back up; this
    // stops waiting and says so.
    const POLL_INTERVAL_MS = 3000;
    const MAX_POLL_MS = 15 * 60 * 1000;
    const startedAt = Date.now();

    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE}/reports/edep-ministry/generation/${pollingGenerationId}`, {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const status = await response.json();
          setGenerationStatus(status);

          if (status.status === 'COMPLETED' || status.status === 'FAILED') {
            setPollingGenerationId(null);
            if (status.status === 'COMPLETED') {
              loadHistory();
            }
            return;
          }
        }

        if (Date.now() - startedAt > MAX_POLL_MS) {
          setPollingGenerationId(null);
          setError(
            "La génération n'a pas répondu depuis 15 minutes. Elle a probablement été interrompue par un redémarrage du serveur — relancez-la.",
          );
          loadHistory();
        }
      } catch (err) {
        console.error('[EDEP] Polling error:', err);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [pollingGenerationId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [campusesData, yearsData] = await Promise.all([
        fetchActiveCampuses(),
        getAcademicYears(),
      ]);
      setCampuses(campusesData.campuses);
      setAcademicYears(yearsData);

      // Set current academic year as default
      // The useEffect will trigger loadMinistryData when selectedAcademicYearId changes
      const currentYear = yearsData.find((y: AcademicYear) => y.isCurrent);
      if (currentYear) {
        setSelectedAcademicYearId(currentYear.id);
      } else if (yearsData.length > 0) {
        // Fallback to first year if no current year
        setSelectedAcademicYearId(yearsData[0].id);
      }
    } catch (err) {
      setError('Impossible de charger les donnees');
      console.error('Error loading initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLatestGeneration = async () => {
    try {
      const response = await fetch(`${API_BASE}/reports/edep-ministry/generation/latest/${selectedMinistryType}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const status = await response.json();
        if (status.status !== 'NONE') {
          setGenerationStatus(status);
          if (status.status === 'PENDING' || status.status === 'PROCESSING') {
            setPollingGenerationId(status.id);
          }
        } else {
          setGenerationStatus(null);
        }
      }
    } catch (err) {
      console.log('[EDEP] Latest generation not available');
    }
  };

  const loadMinistryData = async () => {
    try {
      setLoadingMinistryData(true);
      const params = new URLSearchParams();
      if (selectedCampusId) {
        params.append('campusId', selectedCampusId);
      }
      if (selectedAcademicYearId) {
        params.append('academicYearId', selectedAcademicYearId);
      }

      let endpoint = '';
      if (selectedMinistryType === 'STUDENTS') {
        endpoint = `${API_BASE}/reports/edep-ministry/students/preview?${params.toString()}`;
      } else if (selectedMinistryType === 'TEACHERS') {
        endpoint = `${API_BASE}/reports/edep-ministry/teachers/preview?${params.toString()}`;
      } else {
        endpoint = `${API_BASE}/reports/edep-ministry/admin-staff/preview?${params.toString()}`;
      }

      const response = await fetch(endpoint, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des donnees');
      }

      const responseData = await response.json();
      setMinistryData(responseData.data || []);

      if (responseData.stats) {
        setMinistryStats(responseData.stats);
      } else {
        const complete = (responseData.data || []).filter((s: any) => s.isComplete).length;
        const incomplete = (responseData.data || []).length - complete;
        setMinistryStats({
          total: (responseData.data || []).length,
          complete,
          incomplete,
          completePercentage: (responseData.data || []).length > 0 ? Math.round((complete / (responseData.data || []).length) * 100) : 0,
          incompletePercentage: (responseData.data || []).length > 0 ? Math.round((incomplete / (responseData.data || []).length) * 100) : 0,
        });
      }
    } catch (err: any) {
      console.error('Error loading ministry data:', err);
    } finally {
      setLoadingMinistryData(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await fetch(`${API_BASE}/reports/edep-ministry/history?limit=50`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        // Only show completed generations in history
        setHistory(data.filter((item: MinistryHistoryItem) => item.status === 'COMPLETED'));
      }
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const handleRefresh = async () => {
    await loadMinistryData();
    await loadLatestGeneration();
    setSuccess('Donnees actualisees');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Start ministry document generation
  const handleStartMinistryGeneration = async () => {
    try {
      setGenerating(true);
      setError(null);

      // For all types (STUDENTS, TEACHERS, ADMIN_STAFF), use the ministry endpoint
      const params = new URLSearchParams();
      params.append('reportType', selectedMinistryType);
      if (selectedCampusId) {
        params.append('campusId', selectedCampusId);
      }
      if (selectedAcademicYearId) {
        params.append('academicYearId', selectedAcademicYearId);
      }
      params.append('includeComplete', String(includeMinistryComplete));
      params.append('includeIncomplete', String(includeMinistryIncomplete));

      const response = await fetch(`${API_BASE}/reports/edep-ministry/generate?${params.toString()}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors du demarrage de la generation');
      }

      const result = await response.json();

      setGenerationStatus({
        id: result.id,
        status: 'PENDING',
        progress: 0,
        totalItems: 0,
        processedItems: 0,
        zipFileUrl: null,
        excelFileUrl: null,
        errorMessage: null,
        createdAt: new Date().toISOString(),
        completedAt: null,
        reportType: selectedMinistryType,
      });
      setPollingGenerationId(result.id);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du demarrage de la generation');
    } finally {
      setGenerating(false);
    }
  };

  // The file is not fetched into the page any more (a whole-school ZIP weighs
  // hundreds of MB): the API hands out a short-lived download link and the
  // browser downloads it with its own progress bar. The button shows
  // "Préparation du téléchargement…" until the link is obtained.
  const startDownload = async (generationId: string, fileType: 'zip' | 'excel'): Promise<void> => {
    const response = await fetch(`${API_BASE}/reports/edep-ministry/download-link/${generationId}/${fileType}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Erreur lors du téléchargement');
    }
    const { url } = (await response.json()) as { url: string };
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadGenerated = async (fileType: 'zip' | 'excel') => {
    if (!generationStatus?.id || downloadingMain) return;
    try {
      setDownloadingMain(fileType);
      await startDownload(generationStatus.id, fileType);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du téléchargement');
    } finally {
      // Leave the spinner a moment: the browser's own download bar takes over
      setTimeout(() => setDownloadingMain(null), 1500);
    }
  };

  const handleHistoryDownloadZip = async (generationId: string) => {
    try {
      setDownloadingHistoryId(generationId);
      setDownloadingHistoryType('zip');

      await startDownload(generationId, 'zip');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du téléchargement du ZIP');
    } finally {
      setTimeout(() => {
        setDownloadingHistoryId(null);
        setDownloadingHistoryType(null);
      }, 1500);
    }
  };

  const handleHistoryDownloadExcel = async (generationId: string) => {
    try {
      setDownloadingHistoryId(generationId);
      setDownloadingHistoryType('excel');

      await startDownload(generationId, 'excel');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du téléchargement Excel');
    } finally {
      setTimeout(() => {
        setDownloadingHistoryId(null);
        setDownloadingHistoryType(null);
      }, 1500);
    }
  };

  // Filter ministry data
  const filteredMinistryData = useMemo(() => {
    return ministryData.filter(item => {
      if (includeMinistryComplete && includeMinistryIncomplete) return true;
      if (includeMinistryComplete && item.isComplete) return true;
      if (includeMinistryIncomplete && !item.isComplete) return true;
      return false;
    });
  }, [ministryData, includeMinistryComplete, includeMinistryIncomplete]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Check if user has permission to view this page
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Acces non autorise</h2>
        <p className="text-gray-500">Vous n'avez pas les permissions necessaires pour acceder a cette page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents E-DEP</h1>
          <p className="text-gray-500 mt-1">
            Generez et exportez les dossiers E-DEP au format Ministere
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canViewHistory && (
            <button
              onClick={() => {
                loadHistory();
                setShowHistoryModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <History className="w-4 h-4" />
              Historique
            </button>
          )}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800">Succes</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="w-5 h-5 text-green-600" />
          </button>
        </div>
      )}

      {/* Type Selection */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Type de document</h2>
          <p className="text-sm text-gray-500 mt-1">Selectionnez le type de dossier E-DEP a generer</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(Object.keys(ministryReportTypeConfig) as MinistryReportType[]).map((type) => {
              const typeConfig = ministryReportTypeConfig[type];
              const TypeIcon = typeConfig.icon;
              const isSelected = selectedMinistryType === type;

              return (
                <button
                  key={type}
                  onClick={() => setSelectedMinistryType(type)}
                  className={`
                    bg-white rounded-xl border-2 p-5 text-left transition-all relative
                    ${isSelected ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-100 hover:border-gray-200'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      typeConfig.color === 'emerald' ? 'bg-emerald-100' :
                      typeConfig.color === 'purple' ? 'bg-purple-100' : 'bg-blue-100'
                    }`}>
                      <TypeIcon className={`w-5 h-5 ${
                        typeConfig.color === 'emerald' ? 'text-emerald-600' :
                        typeConfig.color === 'purple' ? 'text-purple-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{typeConfig.label}</p>
                      <p className="text-xs text-gray-500">{typeConfig.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>

            <select
              value={selectedAcademicYearId}
              onChange={(e) => setSelectedAcademicYearId(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {academicYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {formatAcademicYearName(year.name)} {year.isCurrent ? '(actuelle)' : ''}
                </option>
              ))}
            </select>

            <select
              value={selectedCampusId}
              onChange={(e) => setSelectedCampusId(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Tous les campus</option>
              {campuses.map((campus) => (
                <option key={campus.id} value={campus.id}>
                  {campus.name}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeMinistryComplete}
                  onChange={(e) => setIncludeMinistryComplete(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Profils complets</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeMinistryIncomplete}
                  onChange={(e) => setIncludeMinistryIncomplete(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Profils incomplets</span>
              </label>
            </div>
          </div>

          {canGenerate && (
            <button
              onClick={handleStartMinistryGeneration}
              disabled={filteredMinistryData.length === 0 || generating || (generationStatus?.status === 'PROCESSING' || generationStatus?.status === 'PENDING')}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {(generationStatus?.status === 'PROCESSING' || generationStatus?.status === 'PENDING') ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Play size={16} />
              )}
              Generer les dossiers E-DEP
            </button>
          )}
        </div>
      </div>

      {/* Generation Status */}
      {generationStatus && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {generationStatus.status === 'PENDING' || generationStatus.status === 'PROCESSING' ? (
                <>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Loader2 size={20} className="text-blue-600 animate-spin" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Generation en cours...</h3>
                    <p className="text-sm text-gray-500">
                      {generationStatus.processedItems || 0} / {generationStatus.totalItems || '?'} dossiers traites
                      {generationStatus.progress > 0 && ` (${generationStatus.progress}%)`}
                    </p>
                  </div>
                </>
              ) : generationStatus.status === 'COMPLETED' ? (
                <>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 size={20} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Documents E-DEP prets</h3>
                    <p className="text-sm text-gray-500">
                      {generationStatus.totalItems} dossiers generes
                      {generationStatus.completedAt && (
                        <> - Termine le {new Date(generationStatus.completedAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}</>
                      )}
                    </p>
                  </div>
                </>
              ) : generationStatus.status === 'FAILED' ? (
                <>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle size={20} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Echec de la generation</h3>
                    <p className="text-sm text-red-600">
                      {generationStatus.errorMessage || 'Une erreur est survenue'}
                    </p>
                  </div>
                </>
              ) : null}
            </div>

            {generationStatus.status === 'COMPLETED' && canExport && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadGenerated('excel')}
                  disabled={downloadingMain !== null}
                  title="Télécharger le fichier Excel"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait disabled:translate-y-0"
                >
                  {downloadingMain === 'excel' ? <RefreshCw size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
                  {downloadingMain === 'excel' ? 'Préparation du téléchargement…' : 'Excel'}
                </button>
                <button
                  onClick={() => handleDownloadGenerated('zip')}
                  disabled={downloadingMain !== null}
                  title="Télécharger le ZIP des dossiers PDF"
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait disabled:translate-y-0"
                >
                  {downloadingMain === 'zip' ? <RefreshCw size={16} className="animate-spin" /> : <FileArchive size={16} />}
                  {downloadingMain === 'zip' ? 'Préparation du téléchargement…' : 'ZIP (PDFs)'}
                </button>
              </div>
            )}
          </div>

          {(generationStatus.status === 'PENDING' || generationStatus.status === 'PROCESSING') && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${generationStatus.progress || 0}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">
            Apercu des donnees ({filteredMinistryData.length} {ministryReportTypeConfig[selectedMinistryType].label.toLowerCase()})
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Le ZIP contiendra un fichier Excel + un dossier PDF individuel pour chaque personne
          </p>
        </div>

        {loadingMinistryData ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-600 to-indigo-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Statut</th>
                  {ministryReportTypeConfig[selectedMinistryType].columns.slice(0, 8).map((column, index) => (
                    <th
                      key={index}
                      className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMinistryData.slice(0, 20).map((item: any) => (
                  <tr key={item.id} className={!item.isComplete ? 'bg-amber-50' : ''}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {item.isComplete ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                          <CheckCircle2 className="w-3 h-3" />
                          Complet
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                          <AlertTriangle className="w-3 h-3" />
                          Incomplet
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.cnie || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.passport || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.lastName || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.firstName || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.sexe || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.dateNaissance || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.paysOrigine || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.fonction || item.roleLabel || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredMinistryData.length > 20 && (
              <div className="px-4 py-3 bg-gray-50 text-sm text-gray-500 text-center border-t border-gray-200">
                Affichage des 20 premiers resultats sur {filteredMinistryData.length}
              </div>
            )}
            {filteredMinistryData.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                Aucune donnee a afficher avec les filtres selectionnes
              </div>
            )}
          </div>
        )}
      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[85vh] flex flex-col overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <History className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Historique des documents E-DEP</h2>
                    <p className="text-white/70 text-sm">Suivi et tracabilite des rapports generes</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {history.length > 0 ? (
                <div className="space-y-4">
                  {/* Personnel Administratif Section */}
                  {(() => {
                    const staffHistory = history.filter(h => h.reportType === 'STAFF');
                    const staffByYear = staffHistory.reduce((acc, item) => {
                      const year = item.academicYearName || 'Non definie';
                      if (!acc[year]) acc[year] = [];
                      acc[year].push(item);
                      return acc;
                    }, {} as Record<string, MinistryHistoryItem[]>);

                    return (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedSections(prev => ({ ...prev, ADMIN_STAFF: !prev.ADMIN_STAFF }))}
                          className="w-full flex items-center justify-between p-4 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-100 rounded-lg">
                              <UserCog className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900">Personnel Administratif</p>
                              <p className="text-xs text-gray-500">{staffHistory.length} rapport(s)</p>
                            </div>
                          </div>
                          {expandedSections.ADMIN_STAFF ? (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {expandedSections.ADMIN_STAFF && (
                          <div className="p-3 space-y-2 bg-white">
                            {Object.keys(staffByYear).length > 0 ? (
                              Object.entries(staffByYear).map(([year, items]) => {
                                const yearKey = `STAFF_${year}`;
                                return (
                                  <div key={year} className="border border-gray-100 rounded-lg overflow-hidden">
                                    <button
                                      onClick={() => setExpandedYears(prev => ({ ...prev, [yearKey]: !prev[yearKey] }))}
                                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                      <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-500" />
                                        <span className="text-sm font-medium text-gray-700">{year}</span>
                                        <span className="text-xs text-gray-400">({items.length})</span>
                                      </div>
                                      {expandedYears[yearKey] ? (
                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                      )}
                                    </button>
                                    {expandedYears[yearKey] && (
                                      <div className="p-2 space-y-2">
                                        {items.map(item => (
                                          <HistoryItem
                                            key={item.id}
                                            item={item}
                                            downloadingHistoryId={downloadingHistoryId}
                                            downloadingHistoryType={downloadingHistoryType}
                                            onDownloadExcel={handleHistoryDownloadExcel}
                                            onDownloadZip={handleHistoryDownloadZip}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <p className="text-center text-sm text-gray-400 py-4">Aucun rapport</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Enseignants Section */}
                  {(() => {
                    const teachersHistory = history.filter(h => h.reportType === 'TEACHERS');
                    const teachersByYear = teachersHistory.reduce((acc, item) => {
                      const year = item.academicYearName || 'Non definie';
                      if (!acc[year]) acc[year] = [];
                      acc[year].push(item);
                      return acc;
                    }, {} as Record<string, MinistryHistoryItem[]>);

                    return (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedSections(prev => ({ ...prev, TEACHERS: !prev.TEACHERS }))}
                          className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Users className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900">Enseignants</p>
                              <p className="text-xs text-gray-500">{teachersHistory.length} rapport(s)</p>
                            </div>
                          </div>
                          {expandedSections.TEACHERS ? (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {expandedSections.TEACHERS && (
                          <div className="p-3 space-y-2 bg-white">
                            {Object.keys(teachersByYear).length > 0 ? (
                              Object.entries(teachersByYear).map(([year, items]) => {
                                const yearKey = `TEACHERS_${year}`;
                                return (
                                  <div key={year} className="border border-gray-100 rounded-lg overflow-hidden">
                                    <button
                                      onClick={() => setExpandedYears(prev => ({ ...prev, [yearKey]: !prev[yearKey] }))}
                                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                      <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-500" />
                                        <span className="text-sm font-medium text-gray-700">{year}</span>
                                        <span className="text-xs text-gray-400">({items.length})</span>
                                      </div>
                                      {expandedYears[yearKey] ? (
                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                      )}
                                    </button>
                                    {expandedYears[yearKey] && (
                                      <div className="p-2 space-y-2">
                                        {items.map(item => (
                                          <HistoryItem
                                            key={item.id}
                                            item={item}
                                            downloadingHistoryId={downloadingHistoryId}
                                            downloadingHistoryType={downloadingHistoryType}
                                            onDownloadExcel={handleHistoryDownloadExcel}
                                            onDownloadZip={handleHistoryDownloadZip}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <p className="text-center text-sm text-gray-400 py-4">Aucun rapport</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Etudiants Section */}
                  {(() => {
                    const studentsHistory = history.filter(h => h.reportType === 'STUDENTS');
                    const studentsByYear = studentsHistory.reduce((acc, item) => {
                      const year = item.academicYearName || 'Non definie';
                      if (!acc[year]) acc[year] = [];
                      acc[year].push(item);
                      return acc;
                    }, {} as Record<string, MinistryHistoryItem[]>);

                    return (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedSections(prev => ({ ...prev, STUDENTS: !prev.STUDENTS }))}
                          className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <GraduationCap className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900">Etudiants</p>
                              <p className="text-xs text-gray-500">{studentsHistory.length} rapport(s)</p>
                            </div>
                          </div>
                          {expandedSections.STUDENTS ? (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {expandedSections.STUDENTS && (
                          <div className="p-3 space-y-2 bg-white">
                            {Object.keys(studentsByYear).length > 0 ? (
                              Object.entries(studentsByYear).map(([year, items]) => {
                                const yearKey = `STUDENTS_${year}`;
                                return (
                                  <div key={year} className="border border-gray-100 rounded-lg overflow-hidden">
                                    <button
                                      onClick={() => setExpandedYears(prev => ({ ...prev, [yearKey]: !prev[yearKey] }))}
                                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                      <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-500" />
                                        <span className="text-sm font-medium text-gray-700">{year}</span>
                                        <span className="text-xs text-gray-400">({items.length})</span>
                                      </div>
                                      {expandedYears[yearKey] ? (
                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                      )}
                                    </button>
                                    {expandedYears[yearKey] && (
                                      <div className="p-2 space-y-2">
                                        {items.map(item => (
                                          <HistoryItem
                                            key={item.id}
                                            item={item}
                                            downloadingHistoryId={downloadingHistoryId}
                                            downloadingHistoryType={downloadingHistoryType}
                                            onDownloadExcel={handleHistoryDownloadExcel}
                                            onDownloadZip={handleHistoryDownloadZip}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <p className="text-center text-sm text-gray-400 py-4">Aucun rapport</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <div className="p-4 bg-gray-100 rounded-full mb-4">
                    <History className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-lg font-medium">Aucun rapport genere</p>
                  <p className="text-sm text-gray-400 mt-1">Generez votre premier rapport E-DEP</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsEdepPage;
